import {useMemo, useState} from "react";
import {ByTimeInsightProps} from "./ByTimeInsight";
import {getDateFormatterOptions, TimeBucketData, TimeChartData} from "../../chart/TimeChart/TimeChart";
import {
  addTime,
  adjustToTimeZone,
  firstOrUndefined,
  getDefaultTimeFrame,
  getTimeFrameDates,
  getTimeStatisticsRecord,
  isBefore,
  StatisticsGroupWrapper,
  StatisticsType,
  TimeStatisticsWrapper,
  TransportCategory,
  TransportMethod,
  transportMethods
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";

const getValue = (methods: TransportMethod[], type: StatisticsType, rateModel: string | undefined, category: TransportCategory | undefined, entry: TimeStatisticsWrapper | undefined): number | undefined => {
  let value: number | undefined = undefined

  if (entry) {
    for (const method of methods) {
      if (typeof value === 'undefined') {
        value = 0
      }
      value += entry.getValue(method, type, rateModel, category)
    }
  }

  return value
}

export type UseByTimeInsightProps = Omit<ByTimeInsightProps, 'id' | 'title' | 'showEmpty' | 'showSelect' | 'xAxisTitle' | 'yAxisTitle'>

export type UseByTimeInsightResult = {
  chartData?: TimeChartData
  types: StatisticsType[]
  statisticType?: StatisticsType
  setStatisticType: (type: StatisticsType | undefined) => void
}

export function useByTimeInsight(props: UseByTimeInsightProps): UseByTimeInsightResult {
  const {
    fromDttm,
    toDttm = new Date().getTime(),
    data,
    methods,
    splitMethod = true,
    statisticsTypes,
    category,
  } = props

  const {t} = useTranslation(["common"])

  const types: StatisticsType[] = useMemo(() => {
    const types: StatisticsType[] = []
    if (Array.isArray(statisticsTypes)) {
      types.push(...statisticsTypes)
    } else {
      types.push(statisticsTypes)
    }
    return types
  }, [statisticsTypes])

  const [statisticType, setStatisticType] = useState<StatisticsType | undefined>(firstOrUndefined(types))

  const timeFrame = useMemo(() => {
    const defaultTimeFrame = getDefaultTimeFrame(fromDttm, toDttm)
    if (defaultTimeFrame === 'MONTH' || defaultTimeFrame === 'WEEK') {
      return "DAY"
    }
    return defaultTimeFrame
  }, [fromDttm, toDttm])

  const chartData: TimeChartData | undefined = useMemo(() => {
    if (!data) {
      return undefined
    }

    const dates: string[] = []
    const buckets: Record<string, TimeBucketData> = {}

    const timeFrameDates = getTimeFrameDates(timeFrame, fromDttm, toDttm)
    let startDttm = timeFrameDates.startDttm
    const endDttm = timeFrameDates.endDttm

    const formatter = new Intl.DateTimeFormat(undefined, getDateFormatterOptions(timeFrame))

    while (isBefore(startDttm, endDttm)) {
      dates.push(formatter.format(startDttm))

      const stats: StatisticsGroupWrapper[] = []
      if (data) {
        if (Array.isArray(data)) {
          stats.push(...data)
        } else {
          stats.push(data)
        }
      }

      stats?.forEach((stat) => {
        const timeStatsRecord = getTimeStatisticsRecord(timeFrame, stat)
        const timeStats = timeStatsRecord[adjustToTimeZone(startDttm).getTime()]

        const statName = stat.name ?? "Unknown"

        const methodsToProcess: TransportMethod[] = []
        if (methods) {
          if (Array.isArray(methods)) {
            methodsToProcess.push(...methods)
          } else {
            methodsToProcess.push(methods)
          }
        } else {
          methodsToProcess.push(...transportMethods)
        }

        if (splitMethod) {
          for (const method of methodsToProcess) {
            let value = statisticType ? getValue([method], statisticType, undefined, category, timeStats) : undefined
            if (!value) {
              value = 0
            }

            let bucketName = statName;
            if (methodsToProcess.length > 1) {
              bucketName +=  (" - " + t(method.displayValue.id, method.displayValue.defaultMessage))
            }
            if (bucketName in buckets) {
              buckets[bucketName].values.push(value)
            } else {
              buckets[bucketName] = {
                values: [value]
              }
            }
          }
        } else {
          let value = statisticType ? getValue(methodsToProcess, statisticType, undefined, category, timeStats) : undefined
          if (!value) {
            value = 0
          }

          if (statName in buckets) {
            buckets[statName].values.push(value)
          } else {
            buckets[statName] = {
              values: [value]
            }
          }
        }

      })

      startDttm = addTime(timeFrame, startDttm, 1)
    }

    return {
      titles: dates,
      buckets: buckets,
    }
  }, [t, data, timeFrame, fromDttm, toDttm, methods, splitMethod, statisticType, category])

  return {
    chartData: chartData,
    types: types,
    statisticType: statisticType,
    setStatisticType: setStatisticType
  };
}
